import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * Open state of an add / edit dialog. The target is kept after closing, so the dialog
 * keeps its title and values while it animates out.
 */
export function useEditor<T>() {
  const [state, setState] = useState<{ open: boolean; item: T | null }>({
    open: false,
    item: null,
  });
  return {
    open: state.open,
    /** null when adding a new record. */
    item: state.item,
    openNew: () => setState({ open: true, item: null }),
    openEdit: (item: T) => setState({ open: true, item }),
    close: () => setState((current) => ({ ...current, open: false })),
  };
}

/** The value, once it has stopped changing for `delay` ms. */
export function useDebouncedValue<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Whether a media query matches now, kept up to date ("(min-width: 1024px)"). */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}
